import { supabase } from "@/integrations/supabase/client";
import { canTrack, isPrivacySignalSet } from "@/lib/consent";
import {
  isLikelyBot,
  rateLimit,
  underSessionCap,
  EVENT_WINDOW_MS,
  PAGE_VIEW_WINDOW_MS,
} from "@/lib/bot-filter";

const SESSION_KEY = "sfn.sid";
const LANG_KEY = "sfn.lang";

function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid =
        (crypto?.randomUUID?.() as string | undefined) ??
        Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

function getLang(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LANG_KEY);
  } catch {
    return null;
  }
}

export type AnalyticsEvent =
  | "page_view"
  | "language_switch"
  | "cta_click"
  | "doc_download"
  | "doc_open"

  | "faq_open"
  | "modal_open"
  | "modal_close"
  | "quote_loaded"
  | "swap_submitted"
  | "swap_confirmed"
  | "swap_failed"
  | "receipt_copy_hash"
  | "receipt_download"
  | "token_visual_highlight"
  | "vision_card_view"
  | "vision_cta_click"
  | "purchase_preselected"
  | "receipt_viewed"
  | "rpc_failure"
  | "wallet_rejected"
  | "canary_result"
  | "tx_lookup"
  | "receipt_verify_lookup"
  | "price_feed_client_error"
  | "quote_refreshed"
  | "quote_settings_changed"
  | "web_vitals"
  | "perf_alert"
  | "dex_roadmap_click"
  | "dex_badge_click"
  | "quote_auto_refresh";


/**
 * Fire-and-forget privacy-friendly event tracker.
 * No PII, no IP, no fingerprints — just event name, language, path, props,
 * and a per-tab anonymous session id.
 */
export function track(event: AnalyticsEvent, props: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") return;
  // Consent gate: honours Do-Not-Track / Global Privacy Control and requires
  // an explicit opt-in before ANY event leaves the browser.
  if (!canTrack()) return;
  // Bot / spam guard: drop crawler + automation traffic and identical events
  // fired in rapid succession, plus a per-tab hard cap.
  if (isLikelyBot()) return;
  if (!rateLimit(`e:${event}:${JSON.stringify(props).slice(0, 80)}`, EVENT_WINDOW_MS)) return;
  if (!underSessionCap()) return;
  const payload = {
    event,
    props,
    lang: getLang(),
    path: window.location.pathname + window.location.hash,
    session_id: getSessionId(),
  };

  // Don't await — we never want analytics to block UI or throw to callers.
  void supabase
    .from("analytics_events")
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .insert(payload as any)
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        console.debug("[analytics] insert failed:", error.message);
      }
    });
}

/**
 * Silent, anonymous page-view counter.
 * ---------------------------------------------------------------------------
 * Logged once per page load. Stores only: timestamp (server-side default),
 * path, UI language and the referrer HOST (never the full referring URL).
 * No session id, no cookies, no IP, no fingerprints — so it does not require
 * an analytics opt-in. Browser Do-Not-Track / Global Privacy Control signals
 * are still honoured. Nothing is rendered in the public UI.
 */
function referrerHost(): string {
  try {
    const ref = document.referrer;
    if (!ref) return "direct";
    const host = new URL(ref).hostname.replace(/^www\./, "");
    if (host === window.location.hostname.replace(/^www\./, "")) return "internal";
    return host.slice(0, 100);
  } catch {
    return "direct";
  }
}

export function trackPageView(path?: string): void {
  if (typeof window === "undefined") return;
  if (isPrivacySignalSet()) return;
  const p = (path ?? window.location.pathname).slice(0, 300);
  // Bots/crawlers are never counted, and refresh storms on the same path are
  // collapsed into a single view per 30s window per tab.
  if (isLikelyBot()) return;
  if (!rateLimit(`pv:${p}`, PAGE_VIEW_WINDOW_MS)) return;
  void supabase
    .from("analytics_events")
    .insert({
      event: "page_view",
      props: { ref: referrerHost() },
      lang: getLang(),
      path: p,

      session_id: null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        console.debug("[analytics] page_view failed:", error.message);
      }
    });
}
